import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Form, Button } from 'react-bootstrap';
import {
  fetchHVACampaigns,
  HVACampaign,
  fetchHVACampaignUsers,
  assignCampaignUsers
} from 'services/hvaservicecampaign.service';
import { fetchAccounts, Account } from 'services/account.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLockOpen } from '@fortawesome/free-solid-svg-icons';
import FormResultMessage from './FormResultMessage';

const CampaignAssignmentForm: React.FC = () => {
  const [campaigns, setCampaigns] = useState<HVACampaign[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  // eslint-disable-next-line
  const [selectedAccounts, setSelectedAccounts] = useState<Set<number>>(new Set());

  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<
    'successupdate' | 'errorupdate'
  >('successupdate');
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const campaignsData = await fetchHVACampaigns();
        setCampaigns(campaignsData);
      } catch (error) {
        console.error('Error loading campaigns:', error);
      }
    };

    const loadAccounts = async () => {
      try {
        const accountsData = await fetchAccounts();
        // Filtra las cuentas para excluir aquellas con el rol de 'systemadmin'
        const filteredAccounts = accountsData.filter(
          account => !account.roles.includes('systemadmin')
        );
        setAccounts(filteredAccounts);
      } catch (error) {
        console.error('Error loading accounts:', error);
      }
    };

    loadCampaigns();
    loadAccounts();
  }, []);

  useEffect(() => {
    const loadCampaignUsers = async () => {
      if (selectedCampaign !== null) {
        try {
          // eslint-disable-next-line
          const campaignUserIds: number[] = await fetchHVACampaignUsers(selectedCampaign);
          setSelectedAccounts(new Set(campaignUserIds));
        } catch (error) {
          console.error('Error loading campaign users:', error);
        }
      } else {
        setSelectedAccounts(new Set());
      }
    };

    loadCampaignUsers();
  }, [selectedCampaign]);

  const handleCampaignSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCampaign(Number(event.target.value));
  };

  const handleAccountSelect = (accountId: number) => {
    setSelectedAccounts(prevSelectedAccounts => {
      const newSelectedAccounts = new Set(prevSelectedAccounts);
      if (newSelectedAccounts.has(accountId)) {
        newSelectedAccounts.delete(accountId);
      } else {
        newSelectedAccounts.add(accountId);
      }
      return newSelectedAccounts;
    });
  };

  const handleSelectAllAccounts = () => {
    if (selectedAccounts.size === accounts.length) {
      setSelectedAccounts(new Set());
    } else {
      const allAccountIds = new Set(accounts.map(account => account.id));
      setSelectedAccounts(allAccountIds);
    }
  };

  const handleAssign = async () => {
    if (selectedCampaign !== null && selectedAccounts.size > 0) {
      try {
        await assignCampaignUsers(
          selectedCampaign,
          Array.from(selectedAccounts)
        );
        setMessage(
          'Welldone! The selected users have now granted the access to the campaigns,'
        );
        setMessageType('successupdate');
      } catch (error) {
        setMessage('I apologize, but it seems there was a hiccup! ' + error);
        setMessageType('errorupdate');
      }
      setModalVisible(true);
    } else {
      alert('Please select both a campaign and at least one account.');
    }
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  return (
    <Row className="p-0">
      <Col className="">
        <h3 className="px-2 text-secondary-darker">Campaigns</h3>
        <Card className="bg-body">
          <Card.Body>
            <Form.Group>
              <div
                className="scrollbar px-2"
                style={{ height: '250px', overflowY: 'scroll' }}
              >
                {campaigns.map(campaign => (
                  <Form.Check
                    key={campaign.id}
                    type="radio"
                    name="campaigns"
                    id={`campaign-${campaign.id}`}
                    label={`(${campaign.servicename}) - ${campaign.name} `}
                    value={campaign.id}
                    onChange={handleCampaignSelect}
                    checked={selectedCampaign === campaign.id}
                  />
                ))}
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
      </Col>
      <Col className="accounts-column">
        <h3 className="px-2 text-secondary-darker">Users</h3>
        <Card className="bg-body campaignassigment-card">
          <Card.Body>
            <Form.Group>
              <div className="form-check form-switch">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    checked={selectedAccounts.size === accounts.length}
                    onChange={handleSelectAllAccounts}
                    className="form-check-input"
                  />
                  Select All
                </label>
              </div>
              <hr />
              <div
                className="scrollbar px-2"
                style={{ height: '200px', overflowY: 'scroll' }}
              >
                {accounts.map(account => (
                  <Form.Check
                    key={account.id}
                    type="checkbox"
                    label={`${account.fullname} - (${account.email})`}
                    onChange={() => handleAccountSelect(account.id)}
                    checked={selectedAccounts.has(account.id)}
                  />
                ))}
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
        <div className="primary pt-3">
          <Button
            className="btn btn-primary btn-block px-4"
            onClick={handleAssign}
          >
            <FontAwesomeIcon icon={faLockOpen} className="me-2 fs-9" />
            Grant access to the selected users
          </Button>
        </div>
      </Col>
      <FormResultMessage
        show={modalVisible}
        message={message}
        messageType={messageType}
        logout={false}
        onClose={closeModal}
      />
    </Row>
  );
};

export default CampaignAssignmentForm;
